import React from "react";
import { useTable, List, EditButton, ShowButton, DeleteButton, CreateButton } from "@refinedev/antd";
import { Table, Space, Tag } from "antd";
import { useList } from "@refinedev/core";
import {
  DELETE_REQUEST_TEMPLATE_MUTATION,
  REQUEST_TEMPLATE_LIST_QUERY,
} from "./queries";
import { TEAM_LIST_QUERY } from "../teams/queries";

interface IApprovalStep {
  order: number;
  roleSlug: string;
  stepName?: string;
}

interface IPurchaseTemplateRow {
  id: string;
  name: string;
  templateKind?: 'REQUEST' | 'ORDER' | 'RECEIPT' | 'INSPECTION' | 'INVOICE' | 'PLAN' | 'PLAN_REPORT';
  approvalType?: 'local' | 'external';
  externalWorkflow?: any;
  teamId?: string;
  fiscalYear?: number;
  isActive: boolean;
  steps: IApprovalStep[];
  createdAt: string;
  updatedAt: string;
}

export const PurchaseTemplateList: React.FC = () => {
  const { tableProps } = useTable<IPurchaseTemplateRow>({
    resource: "purchaseTemplates",
    initialSorter: [{ field: "createdAt", order: "asc" }],
    initialPageSize: 10,
    meta: {
      gqlQuery: REQUEST_TEMPLATE_LIST_QUERY,
    },
  });

  // Teams for mapping teamId -> team name
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useList<any>({
    resource: "teams",
    meta: { gqlQuery: TEAM_LIST_QUERY },
  });
  const teams =
    Array.isArray((teamsData as any)?.data)
      ? ((teamsData as any)?.data as any[])
      : ((teamsData as any)?.data?.teams?.nodes || []);


  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <CreateButton />
          </Space>
        ),
      }}
    >
      <Table {...tableProps} rowKey="id">
        <Table.Column dataIndex="name" title="Name" sorter />
        <Table.Column
          dataIndex="templateKind"
          title="Type"
          render={(kind: string) => kind || "REQUEST"}
        />
        <Table.Column
          dataIndex="teamId"
          title="Team"
          sorter
          render={(teamId: string | undefined) => {
            if (teamsLoading) return "Loading...";
            if (teamsError) return "Error";
            if (!teamId) return "—";
            const team = (teams as any[]).find((t) => t.id === teamId);
            return team ? team.name : "Unknown";
          }}
        />
        <Table.Column
          dataIndex="fiscalYear"
          title="Fiscal Year"
          sorter
          render={(fy?: number) => (fy ?? "—")}
        />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          sorter
          render={(isActive: boolean) => (
            <Tag color={isActive ? "success" : "default"}>{isActive ? "Active" : "Inactive"}</Tag>
          )}
        />
        <Table.Column
          dataIndex="steps"
          title="Steps"
          render={(steps: IApprovalStep[], record: IPurchaseTemplateRow) => {
            if (record.approvalType === 'external') {
              return record.externalWorkflow?.steps?.length || 0;
            }
            return Array.isArray(steps) ? steps.length : 0;
          }}
        />
        <Table.Column dataIndex="createdAt" title="Created" />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_text: string, record: IPurchaseTemplateRow) => (
            <Space>
              <EditButton size="small" recordItemId={record.id} resource="purchaseTemplates" />
              <ShowButton size="small" recordItemId={record.id} resource="purchaseTemplates" />
              <DeleteButton
                size="small"
                recordItemId={record.id}
                resource="purchaseTemplates"
                     meta={{ gqlMutation: DELETE_REQUEST_TEMPLATE_MUTATION}} 
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};